import axios from 'axios';

// When running in Docker with nginx proxy, REACT_APP_API_URL should be empty
// so requests go to the same origin and nginx proxies /api to backend
const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined 
    ? process.env.REACT_APP_API_URL 
    : 'http://localhost:8000';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

api.interceptors.request.use((config) => {
    // Admin API 用 adminToken，其他用 userToken
    const isAdminApi = config.url && config.url.includes('/api/admin');
    const token = isAdminApi
        ? localStorage.getItem('adminToken')
        : localStorage.getItem('userToken');
    
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            const requestUrl = error.config?.url || '';
            const isAdminApi = requestUrl.includes('/api/admin');
            
            if (isAdminApi) {
                localStorage.removeItem('adminToken');
                if (!window.location.pathname.includes('/admin')) return;
                window.location.href = '/admin';
            } else {
                localStorage.removeItem('userToken');
                localStorage.removeItem('userEmail');
                if (window.location.pathname === '/login') return Promise.reject(error);
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

export const submitBug = async (bugData) => {
    const response = await api.post('/api/bugs', bugData);
    return response.data;
};

export const uploadAttachments = async (bugId, files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    const response = await api.post(`/api/bugs/${bugId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

export const downloadAttachment = async (bugId, s3Key) => {
    const response = await api.get(`/api/bugs/${bugId}/attachments/${s3Key}`, {
        responseType: 'blob',
    });
    return response;
};

export const updateBug = async (bugId, bugData) => {
    const response = await api.put(`/api/bugs/${bugId}`, bugData);
    return response.data;
};

export const deleteAttachment = async (bugId, s3Key) => {
    const response = await api.delete(`/api/bugs/${bugId}/attachments/${s3Key}`);
    return response.data;
};

export const getBugsByEmail = async (email) => {
    const response = await api.get(`/api/bugs/email/${encodeURIComponent(email)}`);
    return response.data;
};

export const getMyBugs = async () => {
    const response = await api.get('/api/bugs/mine');
    return response.data;
};

export const getBugsByIds = async (bugIds) => {
    const response = await api.post('/api/bugs/by-ids', { bug_ids: bugIds });
    return response.data;
};

export const getBugById = async (bugId) => {
    const response = await api.get(`/api/bugs/${bugId}`);
    return response.data;
};

export const adminLogin = async (username, password) => {
    const response = await api.post('/api/admin/login', { username, password });
    return response.data;
};

export const getAllBugs = async (statusFilter = null) => {
    const params = statusFilter ? { status_filter: statusFilter } : {};
    const response = await api.get('/api/admin/bugs', { params });
    return response.data;
};

export const updateBugStatus = async (bugId, status) => {
    const response = await api.patch(`/api/admin/bugs/${bugId}/status`, { status });
    return response.data;
};

export const replyToBug = async (bugId, message) => {
    const response = await api.post(`/api/admin/bugs/${bugId}/reply`, { bug_id: bugId, message });
    return response.data;
};

export const sendBulkEmail = async (subject, message, emails = null) => {
    const response = await api.post('/api/admin/bulk-email', { subject, message, emails });
    return response.data;
};

export const getAllEmails = async () => {
    const response = await api.get('/api/admin/emails');
    return response.data;
};

// User authentication (verification code flow)
export const sendVerificationCode = async (email) => {
    const response = await api.post('/api/auth/send-code', { email });
    return response.data;
};

export const verifyCode = async (email, code, register = false, name = null) => {
    const response = await api.post('/api/auth/verify-code', { email, code, register, name });
    return response.data;
};

export const getCurrentUser = async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
};

// Product management (admin)
export const getAllProducts = async () => {
    const response = await api.get('/api/admin/products');
    return response.data;
};

export const createProduct = async (productData) => {
    const response = await api.post('/api/admin/products', productData);
    return response.data;
};

export const updateProduct = async (productName, productData) => {
    const response = await api.put(`/api/admin/products/${encodeURIComponent(productName)}`, productData);
    return response.data;
};

export const deleteProduct = async (productName) => {
    const response = await api.delete(`/api/admin/products/${encodeURIComponent(productName)}`);
    return response.data;
};

// User management (admin)
export const getAllUsers = async () => {
    const response = await api.get('/api/admin/users');
    return response.data;
};

export const createUser = async (userData) => {
    const response = await api.post('/api/admin/users', userData);
    return response.data;
};

export const updateUser = async (email, userData) => {
    const response = await api.put(`/api/admin/users/${encodeURIComponent(email)}`, userData);
    return response.data;
};

export default api;
